/**
 * SeriesLegend — the one per-series toggle affordance, for every multi-series
 * chart on this page.
 *
 * FR2 was written as an audit-and-unify pass on the premise that `StageLines`
 * and `OutreachLines` both already had toggles. Only `StageLines` did:
 * `OutreachLines` rendered a read-only `<li>` legend with a dot swatch, on both
 * the Dashboard and the Search Progress tab. So this file is one implementation
 * replacing one implementation plus one gap — which is the same destination the
 * FR named, reached from a different starting point.
 *
 * WHY ONE COMPONENT AND NOT TWO CONSISTENT-FOR-NOW ONES. The two charts sit on
 * the same tab, one above the other. Two implementations of "click the legend to
 * hide a line" drift in the details a reader notices first — whether the hidden
 * entry dims or strikes through, whether Space works as well as Enter, whether
 * hiding everything leaves an empty box or says something. They had already
 * drifted to the point of one of them not existing.
 *
 * THE AFFORDANCE IS A BUTTON, NOT A LIST ITEM WITH A CLICK HANDLER. This legend
 * is the chart's control surface and its accessible representation at the same
 * time: every entry is reachable by Tab, operable by Enter and Space for free,
 * and carries `aria-pressed` so its state is announced rather than conveyed by
 * opacity alone.
 *
 * State lives in `useSeriesToggles` — component state, deliberately NOT the URL
 * (FR2.4). A half-hidden chart is not a view anyone links to, and putting it in
 * the query string would compete with the date range, which is.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import { useCallback, useState } from "react";

import { SeriesSwatch } from "./SeriesSwatch";

export interface SeriesLegendEntry {
  /** Stable identity — the stage or contact-method name. */
  key: string;
  /** What the reader sees; the key unless a chart needs to relabel. */
  label: string;
  /** The series' own total over the plotted window. */
  total: number;
  /** Tailwind `stroke-*` utility resolving to a `--chart-*` token. */
  strokeClass: string;
  /** `stroke-dasharray`; empty or absent means solid. */
  dash?: string;
}

export interface SeriesLegendProps {
  entries: readonly SeriesLegendEntry[];
  hidden: ReadonlySet<string>;
  onToggle(key: string): void;
  /** `${testId}-legend` and `${testId}-toggle-<key>` hang off this. */
  testId: string;
  /**
   * False renders the entries as plain text instead of toggles.
   *
   * For the downloaded report, which runs no JavaScript: a button that cannot
   * act is worse than no button, because it invites a press that does nothing.
   * Defaults to `true`, so the live page is untouched by this prop's existence.
   */
  interactive?: boolean;
}

/**
 * The hidden set and its toggle, shared so both charts hide a series the same
 * way. Component state by design (FR2.4) — it dies with the tab, like the rest
 * of a reading position.
 *
 * WHAT THE STATE HOLDS IS A DIFF, NOT THE ANSWER. Since 2026-08-27 a series can
 * start deactivated (`hiddenByDefault`, fed by `seriesDefaults.ts`), and the
 * naive way to express that — seed `useState` with the default-hidden set — is
 * wrong in a way that only shows up on this tab. The series list is a function
 * of the date range, so a stage with no entries inside the current range is
 * absent from it; widening the range brings that stage back, and a set
 * snapshotted at mount has never heard of it, so it would arrive VISIBLE in
 * direct contradiction of the rule the reader can see applied to its neighbours.
 *
 * Keeping the flips instead makes the default a rule rather than a starting
 * position: `hidden = default XOR flipped`, evaluated per key on every render,
 * so a stage that appears later is governed by the same rule as one that was
 * there all along — and a reader who un-hid `Rejected` keeps it un-hidden across
 * a range change, which is the half a re-seeding effect would break in the other
 * direction.
 */
export function useSeriesToggles(
  /** Every key the chart can draw. The domain the returned set is computed over. */
  keys: readonly string[],
  /** True for a key that starts deactivated. Absent means "everything starts shown". */
  hiddenByDefault: (key: string) => boolean = () => false
): {
  hidden: ReadonlySet<string>;
  toggle(key: string): void;
} {
  // The keys the reader has flipped AWAY from their default — not the hidden
  // ones. See the note above.
  const [flipped, setFlipped] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = useCallback((key: string) => {
    setFlipped((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  // Recomputed rather than memoized: `keys` is a fresh array on most renders, so
  // a `useMemo` keyed on it would recompute anyway while costing a dependency
  // list that has to stay honest. The loop is over at most a couple of dozen
  // stage names, and the only consumer of the result is `.has()`.
  const hidden = new Set<string>();
  for (const key of keys) {
    if (hiddenByDefault(key) !== flipped.has(key)) hidden.add(key);
  }

  return { hidden, toggle };
}

/**
 * The FR2.3 state, phrased once.
 *
 * A chart with every series hidden must read as empty BY CHOICE. Both charts
 * keep their axis and their tooltip surface rather than collapsing, so the
 * layout does not jump when the last line goes and come back when it returns —
 * and this sentence says which of the two "nothing here" situations the reader
 * is looking at.
 */
export function allHiddenMessage(noun: string): string {
  return `Every ${noun} is hidden — use the legend to bring one back.`;
}

export function SeriesLegend({
  entries,
  hidden,
  onToggle,
  testId,
  interactive = true,
}: SeriesLegendProps) {
  return (
    <ul
      data-testid={`${testId}-legend`}
      className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-caption text-text-secondary"
    >
      {entries.map((entry) => {
        const shown = !hidden.has(entry.key);
        if (!interactive) {
          // A downloaded document runs no JS, so a toggle is a button that lies.
          // The legend still has to carry the names, the swatches and the totals
          // — it is where each series' real total lives, since the axis is
          // rescaled to whatever is visible.
          return (
            <li key={entry.key} className="flex items-center gap-1.5 px-1 py-0.5">
              <SeriesSwatch strokeClass={entry.strokeClass} dash={entry.dash} />
              {entry.label} <strong className="text-text">{entry.total}</strong>
            </li>
          );
        }
        return (
          <li key={entry.key}>
            {/* No `aria-label`: the visible text IS the name. An override
                reading "Identified stage, 704, shown" would be more words and a
                WCAG 2.5.3 failure, because a speech-input user saying the label
                they can see would no longer match it. `aria-pressed` carries the
                state that opacity alone would leave to sighted readers. */}
            <button
              type="button"
              aria-pressed={shown}
              data-testid={`${testId}-toggle-${entry.key}`}
              onClick={() => onToggle(entry.key)}
              className={`flex items-center gap-1.5 rounded-xs px-1 py-0.5 hover:bg-surface-raised focus:outline-none focus:ring-1 focus:ring-accent ${
                shown ? "" : "opacity-40"
              }`}
            >
              <SeriesSwatch strokeClass={entry.strokeClass} dash={entry.dash} />
              {entry.label} <strong className="text-text">{entry.total}</strong>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
